/* eslint-disable react/no-unescaped-entities, react-hooks/set-state-in-effect */
/**
 * SimulatorPage.js — Diagnostic IA (thème "Dark Luxury")
 * ======================================================
 * Refonte UI alignée sur design_guidelines.json :
 *   • Fond noir profond, cartes #0C0A10, bordures white/[0.08]
 *   • Badge "Généré par Pilote · IA Homly" (plus de mention de modèle)
 *   • Tabs soulignés orange, cartes leviers "Control Room"
 *   • Onglets : Vue IA · Urgences · Source
 */
import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
    Sparkles, Download, TrendingUp, ArrowRight, Info,
    Zap, Clock, UserCheck, RefreshCw, Target, Home, FileSignature,
    Euro, CheckCircle2, AlertCircle, Loader2, Mail,
    AlertTriangle, FileWarning, TrendingDown, Activity, Share2
} from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };
const BODY = { fontFamily: "'IBM Plex Sans', Manrope, sans-serif" };

// Mapping pour les icônes des signaux d'urgence
const SIGNAL_ICONS = {
    AlertTriangle, FileWarning, TrendingDown, Activity, AlertCircle,
};

// Icône par levier pour la cohérence visuelle
const LEVER_ICONS = {
    first_contact_delay_h: Clock,
    five_min_rule_pct: Zap,
    reachability_pct: UserCheck,
    estim_followup_pct: RefreshCw,
    signing_conversion_pct: Target,
    visit_to_offer_pct: Home,
    first_offer_accepted_pct: CheckCircle2,
    offer_discount_pct: Euro,
    compromis_fallthrough_pct: FileSignature,
};

function fmtEUR(n) {
    if (n === null || n === undefined) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

function fmtValue(key, v) {
    if (v === null || v === undefined) return '—';
    if (key === 'first_contact_delay_h') return `${Number(v).toFixed(2)} h`;
    return `${Number(v).toFixed(v % 1 === 0 ? 0 : 1)} %`;
}

// ─── Critical Signal Card (Dark Luxury) ───────────────────────────────
function SignalCard({ signal }) {
    const Icon = SIGNAL_ICONS[signal.icon] || AlertTriangle;
    const sev = signal.severity;
    const accent = sev === 3 ? '#EF4444' : sev === 2 ? '#FF914D' : '#60A5FA';
    const label = sev === 3 ? 'CRITIQUE' : sev === 2 ? 'IMPORTANT' : 'INFO';
    return (
        <div
            className="rounded-r-lg p-4 flex gap-3 border-l-2"
            style={{
                borderColor: accent,
                background: `linear-gradient(90deg, ${accent}0d, transparent)`,
            }}
            data-testid={`signal-${signal.kpi}`}
        >
            <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: `${accent}1a`, color: accent }}
            >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] tracking-[0.2em] uppercase" style={{ ...MONO, color: accent }}>
                        {label}
                    </span>
                    <span className="text-white/20">·</span>
                    <span className="text-sm font-medium text-white" style={DISPLAY}>{signal.label}</span>
                </div>
                <div className="text-sm text-white/80 mb-1.5">{signal.message}</div>
                <div className="text-xs text-white/50 flex items-start gap-1.5">
                    <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: accent }} strokeWidth={1.5} />
                    {signal.action}
                </div>
            </div>
        </div>
    );
}

// ─── Lever card (Dark Luxury "Control Room") ──────────────────────────
function InsightCard({ insight, rank }) {
    const Icon = LEVER_ICONS[insight.lever_key] || Target;
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: rank * 0.08, duration: 0.4 }}
            className="flex flex-col gap-5 bg-[#0C0A10] border border-white/[0.08] rounded-xl p-6 transition-all duration-300 hover:border-[#A855F7]/40 hover:bg-white/[0.02]"
            data-testid={`levier-card-${rank}`}
        >
            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#681793] to-[#A855F7] flex items-center justify-center text-white shrink-0">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[#FF914D]" style={MONO}>
                        Levier #{rank}
                    </div>
                    <h3 className="text-xl text-white tracking-tight leading-tight" style={DISPLAY}>
                        {insight.lever_label}
                    </h3>
                </div>
            </div>

            {/* Actuel → Top 20% */}
            <div className="flex items-center gap-3">
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1" style={MONO}>Actuel</div>
                    <div className="text-lg text-white tabular-nums" style={MONO}>
                        {fmtValue(insight.lever_key, insight.current_value)}
                    </div>
                </div>
                <ArrowRight className="w-4 h-4 text-white/30 flex-shrink-0" strokeWidth={1.5} />
                <div className="flex-1 text-right">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#A855F7] mb-1" style={MONO}>Top 20%</div>
                    <div className="text-lg text-[#C084FC] tabular-nums" style={MONO}>
                        {insight.target_label}
                    </div>
                </div>
            </div>

            {/* Impact metrics (nested darker box) */}
            <div className="bg-[#0A0A0A] border border-white/[0.04] p-4 rounded-lg flex items-center justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1" style={MONO}>Gain estimé / an</div>
                    <div className="text-2xl bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent tabular-nums" style={DISPLAY}>
                        +{fmtEUR(insight.ca_yearly_gain)}
                    </div>
                </div>
                <div className="text-right">
                    <div className="flex items-center justify-end gap-1.5 text-[#FF914D] text-sm" style={MONO}>
                        <TrendingUp className="w-3.5 h-3.5" strokeWidth={1.5} />
                        +{Math.round(insight.extra_closings_yearly)}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-white/40 mt-0.5" style={MONO}>ventes / an</div>
                </div>
            </div>

            {/* Recommandation */}
            <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-[#A855F7]/15 text-[#C084FC] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                </div>
                <p className="text-sm text-white/70 leading-relaxed">
                    {insight.action}
                </p>
            </div>
        </motion.div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────
export default function SimulatorPage() {
    const [diag, setDiag] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [emailing, setEmailing] = useState(false);
    const [sharing, setSharing] = useState(false);
    const [error, setError] = useState(null);

    const [aiNarrativeLoading, setAiNarrativeLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get('/simulator/diagnostic', { timeout: 30000 });
            setDiag(res.data);
            setLoading(false);

            if (!res.data?.ai_narrative) {
                setAiNarrativeLoading(true);
                try {
                    const narrRes = await api.get('/simulator/diagnostic/ai-narrative', { timeout: 30000 });
                    setDiag((prev) => prev ? {
                        ...prev,
                        ai_narrative: narrRes.data?.ai_narrative,
                        ai_source: narrRes.data?.ai_source,
                    } : prev);
                } catch (_e) {
                    // Narrative facultative
                } finally {
                    setAiNarrativeLoading(false);
                }
            }
        } catch (e) {
            setError(e?.response?.data?.detail || 'Impossible de charger le diagnostic');
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const exportPDF = async () => {
        setExporting(true);
        try {
            const res = await api.post('/simulator/export-diagnostic-pdf', null, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Homly_Diagnostic_${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
            toast.success('Diagnostic exporté');
        } catch (e) {
            toast.error("Échec de l'export PDF");
        } finally {
            setExporting(false);
        }
    };

    const sendByEmail = async () => {
        setEmailing(true);
        try {
            const res = await api.post('/simulator/send-diagnostic-email');
            if (res.data?.mocked) {
                toast.success('Email envoyé (mode test : Resend non configuré)');
            } else {
                toast.success("Diagnostic envoyé par email");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Échec de l'envoi");
        } finally {
            setEmailing(false);
        }
    };

    const shareLink = async () => {
        setSharing(true);
        try {
            const res = await api.post('/simulator/share');
            const url = res.data?.share_url;
            if (!url) throw new Error('URL invalide');
            try {
                await navigator.clipboard.writeText(url);
                toast.success('Lien copié — valide 7 jours', { description: url, duration: 6000 });
            } catch {
                toast.success('Lien généré (valide 7 jours)', { description: url, duration: 10000 });
            }
        } catch (e) {
            toast.error("Échec de la génération du lien");
        } finally {
            setSharing(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6 flex flex-col items-center justify-center min-h-[60vh] gap-3" style={BODY} data-testid="simulator-loading">
                <Loader2 className="w-8 h-8 text-[#A855F7] animate-spin" />
                <div className="text-white/80" style={DISPLAY}>L'IA analyse vos KPIs…</div>
                <div className="text-xs text-white/40" style={MONO}>Comparaison avec les benchmarks Top 20 % du réseau</div>
            </div>
        );
    }

    if (error || !diag) {
        return (
            <div className="p-6 max-w-xl mx-auto" style={BODY} data-testid="simulator-error">
                <div className="bg-[#0C0A10] border border-rose-500/30 rounded-xl p-5 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-semibold text-white" style={DISPLAY}>Diagnostic indisponible</div>
                        <div className="text-sm text-white/60 mt-1">{error || 'Erreur inconnue'}</div>
                    </div>
                </div>
            </div>
        );
    }

    const hasInsights = (diag.top_insights || []).length > 0;
    const signalsCount = (diag.critical_signals || []).length;
    const combinedYearly = diag?.combined?.ca_yearly_gain || 0;
    const combinedMonthly = diag?.combined?.ca_monthly_gain || 0;

    return (
        <div className="p-4 md:p-8 max-w-[1280px] mx-auto" style={BODY} data-testid="simulator-page">
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/[0.08] pb-6 mb-8"
            >
                <div>
                    <div className="flex items-center gap-2 text-white/50 mb-1.5">
                        <Sparkles className="w-3 h-3 text-[#FF914D]" strokeWidth={1.5} />
                        <span className="text-xs uppercase tracking-[0.2em]" style={MONO}>Diagnostic IA · Homly</span>
                    </div>
                    <h1 className="text-3xl md:text-4xl font-medium tracking-tight text-white" style={DISPLAY}>
                        Votre plan d'action 90 jours
                    </h1>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button
                        onClick={shareLink}
                        disabled={sharing || !hasInsights}
                        className="bg-transparent border border-white/[0.08] hover:bg-white/[0.04] text-white text-sm h-9"
                        data-testid="share-btn"
                    >
                        <Share2 className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                        {sharing ? 'Génération…' : 'Partager'}
                    </Button>
                    <Button
                        onClick={sendByEmail}
                        disabled={emailing || !hasInsights}
                        className="bg-transparent border border-white/[0.08] hover:bg-white/[0.04] text-white text-sm h-9"
                        data-testid="email-btn"
                    >
                        <Mail className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                        {emailing ? 'Envoi…' : 'Email'}
                    </Button>
                    <Button
                        onClick={exportPDF}
                        disabled={exporting || !hasInsights}
                        className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] hover:opacity-90 text-white text-sm h-9 border-0"
                        data-testid="export-pdf-btn"
                    >
                        <Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                        {exporting ? 'Export…' : 'Exporter PDF'}
                    </Button>
                </div>
            </motion.div>

            {/* Hero opportunité */}
            {hasInsights && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0C0A10] p-6 md:p-8 mb-8"
                    data-testid="impact-hero"
                >
                    <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-gradient-to-br from-[#681793]/25 to-transparent blur-[80px] pointer-events-none" />
                    <div className="relative">
                        <div
                            className="inline-flex items-center gap-2 rounded-full border border-[#A855F7]/30 bg-[#A855F7]/10 px-3 py-1 text-xs text-[#C084FC] mb-4"
                            style={MONO}
                            data-testid="pilote-badge"
                        >
                            <Sparkles className="w-3 h-3" strokeWidth={1.5} />
                            Généré par Pilote · IA Homly
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2 flex items-center gap-1.5" style={MONO}>
                            <TrendingUp className="w-3 h-3" strokeWidth={1.5} />
                            Opportunité détectée
                        </div>
                        <div className="text-4xl md:text-6xl font-medium tracking-tight bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent tabular-nums" style={DISPLAY}>
                            +{fmtEUR(combinedYearly)}
                            <span className="text-xl md:text-2xl font-normal text-white/40 ml-2">/ an</span>
                        </div>
                        <div className="text-sm text-white/50 mt-2" style={MONO}>
                            soit +{fmtEUR(combinedMonthly)} en moyenne / mois
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Tabs */}
            <Tabs defaultValue="ia" className="w-full">
                <TabsList
                    className="w-full justify-start bg-transparent border-b border-white/[0.08] rounded-none p-0 h-auto gap-2"
                    data-testid="simulator-tabs"
                >
                    <TabsTrigger
                        value="ia"
                        className="flex items-center gap-1.5 px-4 pb-3 rounded-none border-b-2 border-transparent text-white/50 data-[state=active]:border-[#FF914D] data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none"
                        data-testid="tab-ia"
                    >
                        <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
                        <span className="text-sm" style={DISPLAY}>Vue IA</span>
                        {hasInsights && (
                            <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#A855F7]/15 text-[#C084FC]" style={MONO}>
                                {diag.top_insights.length}
                            </span>
                        )}
                    </TabsTrigger>
                    <TabsTrigger
                        value="signals"
                        className="flex items-center gap-1.5 px-4 pb-3 rounded-none border-b-2 border-transparent text-white/50 data-[state=active]:border-[#FF914D] data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none"
                        data-testid="tab-signals"
                    >
                        <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} />
                        <span className="text-sm" style={DISPLAY}>Urgences</span>
                        {signalsCount > 0 && (
                            <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#FF914D]/15 text-[#FF914D]" style={MONO}>
                                {signalsCount}
                            </span>
                        )}
                    </TabsTrigger>
                    <TabsTrigger
                        value="source"
                        className="flex items-center gap-1.5 px-4 pb-3 rounded-none border-b-2 border-transparent text-white/50 data-[state=active]:border-[#FF914D] data-[state=active]:bg-transparent data-[state=active]:text-white data-[state=active]:shadow-none"
                        data-testid="tab-source"
                    >
                        <Info className="w-3.5 h-3.5" strokeWidth={1.5} />
                        <span className="text-sm" style={DISPLAY}>Source</span>
                    </TabsTrigger>
                </TabsList>

                {/* Vue IA */}
                <TabsContent value="ia" className="mt-8 space-y-6">
                    {hasInsights && diag.ai_narrative && (
                        <div className="rounded-2xl border border-white/[0.08] bg-[#0C0A10] p-5">
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-lg bg-[#A855F7]/15 text-[#C084FC] flex items-center justify-center flex-shrink-0">
                                    <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5" style={MONO}>
                                        Synthèse Pilote
                                    </p>
                                    <blockquote className="text-sm md:text-base italic leading-relaxed text-white/80 border-l-2 border-[#A855F7]/40 pl-3 m-0">
                                        « {diag.ai_narrative} »
                                    </blockquote>
                                </div>
                            </div>
                        </div>
                    )}

                    {hasInsights ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {diag.top_insights.map((ins, idx) => (
                                <InsightCard key={ins.lever_key} insight={ins} rank={idx + 1} />
                            ))}
                        </div>
                    ) : (
                        <div
                            className="bg-[#0C0A10] border border-emerald-500/20 rounded-2xl p-8 text-center"
                            data-testid="all-good"
                        >
                            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-400 mb-3">
                                <CheckCircle2 className="w-7 h-7" strokeWidth={1.5} />
                            </div>
                            <h2 className="text-lg font-medium text-white mb-1" style={DISPLAY}>
                                Tous vos KPIs sont au niveau Top 20 %
                            </h2>
                            <p className="text-sm text-white/50 max-w-md mx-auto">
                                Continuez à alimenter les KPIs pour un diagnostic encore plus précis.
                            </p>
                        </div>
                    )}
                </TabsContent>

                {/* Urgences */}
                <TabsContent value="signals" className="mt-8">
                    {signalsCount > 0 ? (
                        <div className="flex flex-col gap-4" data-testid="critical-signals-section">
                            {diag.critical_signals.map((s, i) => (
                                <SignalCard key={i} signal={s} />
                            ))}
                        </div>
                    ) : (
                        <div className="bg-[#0C0A10] border border-emerald-500/20 rounded-2xl p-8 text-center">
                            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" strokeWidth={1.5} />
                            <h3 className="text-base font-medium text-white mb-1" style={DISPLAY}>Aucune urgence détectée</h3>
                            <p className="text-sm text-white/50">L'opérationnel est sous contrôle.</p>
                        </div>
                    )}
                </TabsContent>

                {/* Source & calcul */}
                <TabsContent value="source" className="mt-8">
                    <div className="bg-[#0C0A10] border border-white/[0.08] rounded-xl p-6 text-sm text-white/60">
                        <div className="flex items-start gap-3">
                            <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-white/40" strokeWidth={1.5} />
                            <div className="space-y-3 leading-relaxed">
                                <p>
                                    <strong className="text-white">Périmètre :</strong> {diag.baseline?.mandate_count || 0} mandats, {diag.baseline?.estim_count || 0} estimations
                                    pris en compte sur la période d'analyse.
                                </p>
                                <p>
                                    <strong className="text-white">Méthodologie :</strong> Comparaison de vos KPIs réels avec les benchmarks Top 20 % du réseau Homly.
                                    Chaque levier identifie un écart corrigeable, le gain CA/an est extrapolé sur la base de votre volume actuel.
                                </p>
                                <p>
                                    <strong className="text-white">Limites :</strong> Les gains chiffrés supposent une application rigoureuse des actions recommandées —
                                    ils sont indicatifs et n'engagent pas l'agence.
                                </p>
                                {diag.ai_narrative && (
                                    <p className="inline-flex items-center gap-1.5 text-[#C084FC]" style={MONO}>
                                        <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Narratif généré par Pilote · IA Homly
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
